import { Fragment, useMemo, useState } from 'react';
import {
  ChevronDown,
  FileSignature,
  ShieldCheck,
  LayoutGrid,
  Pencil,
  Plus,
  QrCode,
  RotateCcw,
  CalendarDays,
  Rows3,
  SlidersHorizontal,
  Upload,
  X,
  UserX,
  Trash2,
  Send,
  Inbox,
} from 'lucide-react';
import {
  AdminPage,
  Button,
  EmptyState,
  ErrorState,
  Panel,
  Progress,
  Skeleton,
  StatusBadge,
  Table,
  Td,
  Th,
  Tr,
  useConfirm,
} from '@/design/components';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { usePerm } from '@/access/usePerm';
import { useModule } from '@/access/useModule';
import { useMe } from '@/auth/useMe';
import { formatPhone } from '@/lib/phone';
import { formatDate, toDateKey } from '@/lib/dates';
import { useScheduleHours, type HoursRow } from '@/features/schedule/hoursApi';
import { usePendingRequestsCount } from '@/features/requests/api';
import { DirectMessageButton } from '@/features/messages/DirectMessageButton';
import { ContractsPanel } from './private/ContractsPanel';
import { PrivateDetails } from './private/PrivateDetails';
import { errorMessage } from '@/lib/errors';
import {
  useEmployeeMutations,
  useEmployeeOptions,
  useEmployeeSales,
  useEmployees,
  type EmployeeListItem,
} from './api';
import { EmployeeForm, type EmployeeFormResult } from './EmployeeForm';
import { usePendingApplicationsCount } from './join/api';
import { EmployeeRequests } from './EmployeeRequests';
import {
  COLUMN_LABELS,
  DEFAULT_COLUMNS,
  SALES_COLUMNS,
  cellValue,
  hoursLabel,
  salesLabel,
  readColumns,
  saveColumns,
  type ColumnKey,
  type EmployeeHours,
  type EmployeeSales,
} from './columns';

const FIELD = 'min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/** רשימת עובדים עם סינון לפי סניף, תפקיד ופעיל (PRD 5.2). */
export function EmployeesPage() {
  const canManage = usePerm('employees.manage');
  // קוד הקופה רלוונטי רק כשיש חיבור לקופות. בלעדיו זו עמודה
  // שכולה מקפים, בטבלה שכבר נגללת לרוחב.
  const showComaxCode = useModule('integration');
  // שני ה hooks נקראים תמיד: && היה מדלג על השני ומשנה את סדר
  // ה hooks בין רינדורים
  const canViewSales = usePerm('sales.view');
  const salesModuleOn = useModule('sales');
  const canSeeSales = canViewSales && salesModuleOn;

  // בקשות עברו לכאן, ולכן גם ההרשאה שלהן נבדקת כאן: מי שמנהל
  // עובדים אינו בהכרח מי שמאשר להם חופשה
  const canManageHr = usePerm('hr.manage');
  const canPrivate = usePerm('hr.private');
  const hrDocsModuleOn = useModule('hr_docs');
  const requestsModuleOn = useModule('ops_requests');
  const canSeeRequests = canManageHr && requestsModuleOn;
  // מסמכים אישיים: מפתח נפרד מ hr.manage, כי מנהלי סניף מחזיקים
  // את זה ואין להם עסק עם ת"ז ופרטי בנק (מיגרציה 092)
  const canSeePrivate = canPrivate && hrDocsModuleOn;

  const { me } = useMe();
  const { data: employees, isPending, isError, refetch } = useEmployees();
  const { data: options } = useEmployeeOptions();
  const { create, update, setBranches, setEmploymentEnd, remove, sendWelcome } =
    useEmployeeMutations();
  const confirm = useConfirm();

  const [branchId, setBranchId] = useState('');
  const [jobTitleId, setJobTitleId] = useState('');
  const [roleId, setRoleId] = useState('');
  const [notJoined, setNotJoined] = useState(false);
  const [view, setView] = useState<'list' | 'branches'>(() => readView());
  const [columns, setColumns] = useState<ColumnKey[]>(() => readColumns());
  const [isPickingColumns, setIsPickingColumns] = useState(false);
  const [endingFor, setEndingFor] = useState<EmployeeListItem | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /**
   * שליחת הודעת קליטה. force נשלח רק כשכבר נשלחה בעבר, כלומר
   * כשהלחיצה היא בקשה מודעת לשלוח שוב.
   */
  const welcome = (employee: EmployeeListItem) => {
    setError(null);
    setNotice(null);
    sendWelcome
      .mutateAsync({ employeeId: employee.id, force: Boolean(employee.invited_at) })
      .then((result) => {
        setNotice(
          result?.data?.sent
            ? `הודעת קליטה נשלחה ל${employee.first_name}.`
            : 'ההודעה כבר נשלחה בעבר.',
        );
      })
      .catch((caught: unknown) => setError(errorMessage(caught)));
  };

  /**
   * המחיקה נשאלת תמיד, והשרת הוא שקובע אם מותר. הכלל מוסבר כאן
   * מראש כדי שהסירוב לא יגיע כהפתעה.
   */
  const askDelete = (employee: EmployeeListItem) =>
    confirm.ask({
      title: 'מחיקת כרטיס עובד',
      body: (
        <>
          <p className="m-0">
            {employee.first_name} {employee.last_name}
          </p>
          <p className="m-0 text-body-sm text-muted">
            אפשר למחוק רק כרטיס שנפתח בטעות: מי שכבר נכנס למערכת או שיש לו רישומים נשאר, ולו מסיימים
            העסקה.
          </p>
        </>
      ),
      confirmLabel: 'מחיקת הכרטיס',
      onConfirm: () => {
        setError(null);
        remove.mutateAsync(employee.id).catch((caught: unknown) => setError(errorMessage(caught)));
      },
    });
  const pendingApplications = usePendingApplicationsCount();
  const pendingRequests = usePendingRequestsCount();

  /** אילו עמודות אפשר בכלל לבחור, לפי המודולים וההרשאות. */
  const available = useMemo(
    () =>
      (Object.keys(COLUMN_LABELS) as ColumnKey[]).filter((key) => {
        if (key === 'comax') return showComaxCode;
        if (SALES_COLUMNS.includes(key)) return canSeeSales;
        return true;
      }),
    [showComaxCode, canSeeSales],
  );

  const shown = useMemo(
    () => columns.filter((key) => available.includes(key)),
    [columns, available],
  );

  const salesShown = shown.some((key) => SALES_COLUMNS.includes(key));
  const sales = useEmployeeSales(salesShown);

  // שעות החודש הנוכחי. נטענות רק כשהעמודה מוצגת.
  const thisMonth = useMemo(() => currentMonthRange(), []);
  const hours = useScheduleHours(thisMonth.from, thisMonth.to, shown.includes('hours'));

  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  /** השורה הפתוחה, ואם היא במצב עריכה. מזהה ולא אובייקט: הרשימה
   *  מתרעננת אחרי שמירה, ואובייקט שמור היה נשאר על נתונים ישנים. */
  const [openId, setOpenId] = useState<string | null>(null);
  const [isEditingOpen, setIsEditingOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const visible = useMemo(() => {
    const term = search.trim();
    return (employees ?? []).filter((employee) => {
      if (!showInactive && !employee.is_active) return false;
      // סינון לפי סניף תופס גם מי שמשויך אליו כסניף נוסף, אחרת
      // עובד שמסתובב בין שניים נעלם מהסניף שהוא באמת עובד בו
      if (branchId) {
        const inBranch =
          employee.primary_branch_id === branchId ||
          (employee.employee_branches ?? []).some((row) => row.branch_id === branchId);
        if (!inBranch) return false;
      }
      if (jobTitleId && employee.job_titles?.id !== jobTitleId) return false;
      if (roleId && employee.roles?.id !== roleId) return false;
      if (notJoined && employee.auth_user_id !== null) return false;
      if (term) {
        // הטלפון מאוחסן כ +972 ומוצג כ 050-700-0004. בלי שתי
        // הצורות, מי שמקליד בדיוק את מה שהוא רואה על המסך לא
        // מוצא כלום.
        const haystack = [
          employee.first_name,
          employee.last_name,
          employee.phone,
          formatPhone(employee.phone),
          employee.phone.replace(/\D/g, ''),
          formatPhone(employee.phone).replace(/\D/g, ''),
        ]
          .join(' ')
          .toLowerCase();
        const needle = term.toLowerCase();
        const digits = term.replace(/\D/g, '');
        if (!haystack.includes(needle) && !(digits && haystack.includes(digits))) return false;
      }
      return true;
    });
  }, [employees, search, branchId, jobTitleId, roleId, notJoined, showInactive]);

  /**
   * קיבוץ לפי סניף. עובד שמשויך לכמה סניפים מופיע בכל אחד מהם,
   * וזו לא כפילות אלא התשובה לשאלה "מי אצלי בסניף היום".
   *
   * מי שאין לו סניף כלל מקבל קבוצה משלו בסוף, כי אחרת הוא נעלם
   * מהמסך בלי שאף אחד ישים לב.
   */
  const groups = useMemo(() => {
    const byBranch = new Map<
      string,
      { name: string; color: string | null; employees: EmployeeListItem[] }
    >();
    for (const branch of options?.branches ?? []) {
      byBranch.set(branch.id, { name: branch.name, color: branch.color, employees: [] });
    }

    const orphans: EmployeeListItem[] = [];

    for (const employee of visible) {
      const ids = new Set<string>();
      if (employee.primary_branch_id) ids.add(employee.primary_branch_id);
      for (const row of employee.employee_branches ?? []) ids.add(row.branch_id);

      if (ids.size === 0) {
        orphans.push(employee);
        continue;
      }
      for (const id of ids) byBranch.get(id)?.employees.push(employee);
    }

    const result = [...byBranch].map(([id, group]) => ({ id, ...group }));
    if (orphans.length > 0) {
      result.push({ id: 'none', name: 'ללא סניף', color: null, employees: orphans });
    }
    return result;
  }, [visible, options?.branches]);

  /** פתיחת שורה סוגרת את הקודמת, ותמיד חוזרת למצב צפייה. */
  function toggleRow(id: string) {
    setIsEditingOpen(false);
    setOpenId((current) => (current === id ? null : id));
  }

  async function saveEmployee(
    employee: EmployeeListItem,
    { values, extraBranchIds }: EmployeeFormResult,
  ) {
    await update.mutateAsync({ id: employee.id, values });
    await setBranches.mutateAsync({ employeeId: employee.id, branchIds: extraBranchIds });
    setIsEditingOpen(false);
  }

  /** עמודות הטבלה, לפריסת שורת הפרטים מתחתיה. */
  const columnCount = 1 + shown.length;

  async function run(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  const isEditingSomething = isAdding;
  const total = employees?.length ?? 0;

  return (
    <AdminPage
      title="עובדים"
      subtitle={visible.length === total ? `${total} עובדים` : `${visible.length} מתוך ${total}`}
      actions={
        canManage && !isEditingSomething ? (
          <>
            {/*
              בקשות אינן טאב משלהן. הן בקשות של עובדים, ומי שמטפל
              בהן כבר נמצא כאן — ואותו מנהל רואה גם את הבקשות של
              עובד אחד בכרטיס שלו.
            */}
            {canSeeRequests && (
              <Link to="/admin/employees/requests" className="no-underline">
                <Button variant={pendingRequests.data ? 'accent' : 'secondary'}>
                  <Inbox size={20} strokeWidth={1.75} aria-hidden="true" />
                  בקשות
                  {pendingRequests.data
                    ? pendingRequests.data === 1
                      ? ' · אחת ממתינה'
                      : ` · ${pendingRequests.data} ממתינות`
                    : ''}
                </Button>
              </Link>
            )}
            {/* תבניות החוזים: כפתור בסרגל ולא פריט תפריט, כמו
                בקשות — זה אותו תחום ואותו מסך שחוזרים אליו */}
            {canSeePrivate && (
              <Link to="/admin/employees/contract-templates" className="no-underline">
                <Button variant="secondary">
                  <FileSignature size={20} strokeWidth={1.75} aria-hidden="true" />
                  תבניות חוזים
                </Button>
              </Link>
            )}
            <Link to="/admin/employees/applications" className="no-underline">
              {/* המונה כאן ולא בסרגל: אחרי שהטאב הנפרד אוחד לתוך
                  העובדים, זו הנקודה היחידה שממנה רואים שמחכות
                  בקשות לאישור */}
              <Button variant={pendingApplications.data ? 'accent' : 'secondary'}>
                <QrCode size={20} strokeWidth={1.75} aria-hidden="true" />
                הרשמה ב QR
                {pendingApplications.data
                  ? pendingApplications.data === 1
                    ? ' · בקשה אחת ממתינה'
                    : ` · ${pendingApplications.data} בקשות ממתינות`
                  : ''}
              </Button>
            </Link>
            <Link to="/admin/employees/import" className="no-underline">
              <Button variant="secondary">
                <Upload size={20} strokeWidth={1.75} aria-hidden="true" />
                ייבוא מקובץ
              </Button>
            </Link>
            {view === 'list' && (
              <Button variant="secondary" onClick={() => setIsPickingColumns(true)}>
                <SlidersHorizontal size={20} strokeWidth={1.75} aria-hidden="true" />
                עמודות
              </Button>
            )}
            <ViewToggle
              value={view}
              onChange={(next) => {
                setView(next);
                saveView(next);
              }}
            />
            <Button variant="primary" onClick={() => setIsAdding(true)}>
              <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
              הוספת עובד/ת
            </Button>
          </>
        ) : undefined
      }
    >
      {isAdding && (
        <Panel title="עובד/ת חדש/ה">
          <EmployeeForm
            onCancel={() => setIsAdding(false)}
            onSubmit={async ({ values, extraBranchIds }) => {
              const id = await create.mutateAsync(values);
              if (extraBranchIds.length > 0) {
                await setBranches.mutateAsync({ employeeId: id, branchIds: extraBranchIds });
              }
              setIsAdding(false);

              /**
               * הודעת הקליטה נשלחת מיד, ולא מחכה שמישהו יזכור.
               *
               * כשליחה שדורשת לחיצה נפרדת היא פשוט לא קורית, והעובד
               * החדש לא יודע שיש אפליקציה. כישלון כאן אינו מבטל את
               * יצירת העובד — הוא נאמר, ואפשר לשלוח שוב מהכרטיס.
               */
              setNotice(null);
              try {
                const result = await sendWelcome.mutateAsync({ employeeId: id });
                setNotice(
                  result?.data?.sent
                    ? `${values.first_name} נוסף/ה, והודעת הקליטה נשלחה.`
                    : `${values.first_name} נוסף/ה. הודעת הקליטה לא נשלחה.`,
                );
              } catch (caught) {
                setError(
                  `${values.first_name} נוסף/ה, אבל הודעת הקליטה לא נשלחה: ${errorMessage(caught)}`,
                );
              }
            }}
          />
        </Panel>
      )}

      {endingFor && (
        <EmploymentEndDialog
          employee={endingFor}
          onClose={() => setEndingFor(null)}
          onConfirm={(date: string | null) => {
            const target = endingFor;
            setEndingFor(null);
            run(() => setEmploymentEnd.mutateAsync({ employeeId: target.id, date }));
          }}
        />
      )}

      {isPickingColumns && (
        <ColumnsDialog
          available={available}
          shown={shown}
          onToggle={(key, on) =>
            setColumns((prev) => {
              const next = on ? [...prev, key] : prev.filter((k) => k !== key);
              saveColumns(next);
              return next;
            })
          }
          onReset={() => {
            setColumns(DEFAULT_COLUMNS);
            saveColumns(DEFAULT_COLUMNS);
          }}
          onClose={() => setIsPickingColumns(false)}
        />
      )}

      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {notice && (
        <p role="status" className="m-0 text-body-sm text-good">
          {notice}
        </p>
      )}

      <Panel bodyClassName="p-0">
        <div className="flex flex-wrap items-end gap-3 border-b border-line p-4">
          <div className="grid min-w-56 flex-1 gap-1 sm:max-w-80">
            <label htmlFor="employee-search" className="text-label font-medium">
              חיפוש
            </label>
            <input
              id="employee-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="שם או טלפון"
              className={FIELD}
            />
          </div>

          <div className="grid min-w-44 gap-1">
            <label htmlFor="employee-branch" className="text-label font-medium">
              סניף
            </label>
            <select
              id="employee-branch"
              value={branchId}
              onChange={(event) => setBranchId(event.target.value)}
              className={FIELD}
            >
              <option value="">כל הסניפים</option>
              {(options?.branches ?? []).map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid min-w-44 gap-1">
            <label htmlFor="employee-job-title" className="text-label font-medium">
              תפקיד
            </label>
            <select
              id="employee-job-title"
              value={jobTitleId}
              onChange={(event) => setJobTitleId(event.target.value)}
              className={FIELD}
            >
              <option value="">כל התפקידים</option>
              {(options?.jobTitles ?? []).map((title) => (
                <option key={title.id} value={title.id}>
                  {title.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid min-w-44 gap-1">
            <label htmlFor="employee-role" className="text-label font-medium">
              הרשאות
            </label>
            <select
              id="employee-role"
              value={roleId}
              onChange={(event) => setRoleId(event.target.value)}
              className={FIELD}
            >
              <option value="">כל ההרשאות</option>
              {(options?.roles ?? []).map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>

          <label className="flex min-h-11 items-center gap-2 text-body">
            <input
              type="checkbox"
              checked={notJoined}
              onChange={(event) => setNotJoined(event.target.checked)}
              className="size-5"
            />
            לא פעיל באפליקציה
          </label>

          <label className="flex min-h-11 items-center gap-2 text-body">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(event) => setShowInactive(event.target.checked)}
              className="size-5"
            />
            להציג גם עובדים לא פעילים
          </label>
        </div>

        {isPending && (
          <div className="grid gap-2 p-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}

        {isError && (
          <div className="p-4">
            <ErrorState onRetry={() => void refetch()} />
          </div>
        )}

        {employees && visible.length === 0 && (
          <div className="p-4">
            <EmptyState
              title="אין עובדים שמתאימים לסינון"
              description="אפשר לנקות את החיפוש או לבחור סניף אחר."
            />
          </div>
        )}

        {visible.length > 0 && view === 'branches' && (
          <div className="grid gap-3 p-4 lg:grid-cols-2 2xl:grid-cols-3">
            {groups.map((group) => (
              <BranchGroup
                key={group.id}
                name={group.name}
                color={group.color}
                employees={group.employees}
                canManage={canManage}
                canSeeRequests={canSeeRequests}
                canSeePrivate={canSeePrivate}
                selfId={me?.employeeId}
                openId={openId}
                isEditingOpen={isEditingOpen}
                onToggleRow={toggleRow}
                onEdit={() => setIsEditingOpen(true)}
                onCancelEdit={() => setIsEditingOpen(false)}
                onSave={saveEmployee}
                onEndEmployment={setEndingFor}
                onDelete={askDelete}
                onWelcome={welcome}
              />
            ))}
          </div>
        )}

        {visible.length > 0 && view === 'list' && (
          <>
            {/*
              דסקטופ: טבלה. ההשוואה בין שורות היא העבודה האמיתית של
              המסך הזה, וכרטיסים מסתירים אותה.
            */}
            <div className="hidden md:block">
              <Table>
                <thead>
                  <tr>
                    <Th>שם</Th>
                    {shown.map((key) => (
                      <Th key={key}>{COLUMN_LABELS[key]}</Th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((employee) => {
                    const isOpen = openId === employee.id;
                    return (
                      <Fragment key={employee.id}>
                        <Tr onClick={() => toggleRow(employee.id)}>
                          <Td className="font-medium">
                            <span className="inline-flex items-center gap-2">
                              <ChevronDown
                                size={16}
                                strokeWidth={1.75}
                                aria-hidden="true"
                                className={cn(
                                  'shrink-0 text-muted transition-transform',
                                  isOpen && 'rotate-180',
                                )}
                              />
                              {employee.first_name} {employee.last_name}
                              {employee.id === me?.employeeId && (
                                <span className="text-label text-muted">(את/ה)</span>
                              )}
                            </span>
                          </Td>
                          {shown.map((key) => (
                            <EmployeeCell
                              key={key}
                              column={key}
                              employee={employee}
                              sales={sales.data?.[employee.id]}
                              hours={hoursFor(hours.data?.get(employee.id))}
                            />
                          ))}
                        </Tr>

                        {isOpen && (
                          <tr>
                            <td colSpan={columnCount} className="bg-cream p-0">
                              <div className="p-4">
                                <EmployeePanel
                                  employee={employee}
                                  isEditing={isEditingOpen}
                                  canManage={canManage}
                                  canSeeRequests={canSeeRequests}
                                  canSeePrivate={canSeePrivate}
                                  isSelf={employee.id === me?.employeeId}
                                  onEdit={() => setIsEditingOpen(true)}
                                  onCancelEdit={() => setIsEditingOpen(false)}
                                  onEndEmployment={() => setEndingFor(employee)}
                                  onDelete={() => askDelete(employee)}
                                  onWelcome={() => welcome(employee)}
                                  onSave={saveEmployee}
                                />
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            {/* טלפון: כרטיסים. טבלה בשבע עמודות לא נקראת שם */}
            <ul className="grid list-none gap-2 p-4 md:hidden">
              {visible.map((employee) => (
                <li
                  key={employee.id}
                  className="grid gap-2 rounded-md border border-line bg-raised p-3"
                >
                  <button
                    type="button"
                    onClick={() => toggleRow(employee.id)}
                    aria-expanded={openId === employee.id}
                    className="grid cursor-pointer gap-1 bg-transparent p-0 text-start"
                  >
                    <span className="flex flex-wrap items-center gap-2 text-title font-medium">
                      <ChevronDown
                        size={16}
                        strokeWidth={1.75}
                        aria-hidden="true"
                        className={cn(
                          'shrink-0 text-muted transition-transform',
                          openId === employee.id && 'rotate-180',
                        )}
                      />
                      {employee.first_name} {employee.last_name}
                      {employee.id === me?.employeeId && (
                        <span className="text-body-sm text-muted">(את/ה)</span>
                      )}
                      <EmployeeStatus employee={employee} />
                    </span>
                    <span className="text-body-sm text-muted" dir="ltr">
                      {formatPhone(employee.phone)}
                    </span>
                    <span className="text-body-sm text-muted">
                      {[employee.branches?.name, employee.job_titles?.name, employee.roles?.name]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </button>

                  {openId === employee.id && (
                    <EmployeePanel
                      employee={employee}
                      isEditing={isEditingOpen}
                      canManage={canManage}
                      canSeeRequests={canSeeRequests}
                      canSeePrivate={canSeePrivate}
                      isSelf={employee.id === me?.employeeId}
                      onEdit={() => setIsEditingOpen(true)}
                      onCancelEdit={() => setIsEditingOpen(false)}
                      onEndEmployment={() => setEndingFor(employee)}
                      onDelete={() => askDelete(employee)}
                      onWelcome={() => welcome(employee)}
                      onSave={saveEmployee}
                    />
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </Panel>
      {confirm.dialog}
    </AdminPage>
  );
}

function EmployeeStatus({ employee }: { employee: EmployeeListItem }) {
  if (!employee.is_active) return <StatusBadge tone="warn">העסקה הסתיימה</StatusBadge>;
  if (employee.employment_end_date) {
    // עדיין עובד/ת, אבל התאריך כבר ידוע. מוצג כדי שלא יישכח.
    return (
      <StatusBadge tone="warn">מסיים/ת ב {formatDate(employee.employment_end_date)}</StatusBadge>
    );
  }
  if (!employee.auth_user_id) return <StatusBadge tone="warn">עוד לא נכנס/ה</StatusBadge>;
  return <StatusBadge tone="good">פעיל</StatusBadge>;
}

function BranchNames({ employee }: { employee: EmployeeListItem }) {
  const extra = (employee.employee_branches ?? [])
    .map((row) => row.branches?.name)
    .filter((name): name is string => Boolean(name) && name !== employee.branches?.name);

  if (!employee.branches?.name && extra.length === 0) return <>—</>;

  return (
    <span className="grid gap-0.5">
      <span>{employee.branches?.name ?? '—'}</span>
      {extra.length > 0 && <span className="text-label">גם {extra.join(', ')}</span>}
    </span>
  );
}

const VIEW_KEY = 'employees-view';

/** הבחירה נזכרת בדפדפן הזה בלבד. נוחות, ולא נתון של המערכת. */
function readView(): 'list' | 'branches' {
  try {
    return localStorage.getItem(VIEW_KEY) === 'branches' ? 'branches' : 'list';
  } catch {
    return 'list';
  }
}

function saveView(value: 'list' | 'branches') {
  try {
    localStorage.setItem(VIEW_KEY, value);
  } catch {
    // חלון פרטי. הבחירה פשוט לא תיזכר, וזה בסדר.
  }
}

function ViewToggle({
  value,
  onChange,
}: {
  value: 'list' | 'branches';
  onChange: (next: 'list' | 'branches') => void;
}) {
  const options = [
    { key: 'list' as const, label: 'רשימה', icon: Rows3 },
    { key: 'branches' as const, label: 'לפי סניף', icon: LayoutGrid },
  ];

  return (
    <div className="inline-flex rounded-sm border border-muted" role="group" aria-label="תצוגה">
      {options.map((option) => {
        const isActive = value === option.key;
        return (
          <button
            key={option.key}
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(option.key)}
            className={cn(
              'inline-flex min-h-11 cursor-pointer items-center gap-2 px-3 text-body-sm font-medium transition-colors',
              isActive ? 'bg-charcoal text-on-charcoal' : 'bg-transparent text-ink hover:bg-cream',
            )}
          >
            <option.icon size={18} strokeWidth={1.75} aria-hidden="true" />
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * סניף אחד וכל מי שעובד בו. הכותרת אומרת כמה אנשים, וכמה מהם עוד
 * לא נכנסו למערכת: זה המספר שמעניין לפני עלייה לאוויר.
 */
function BranchGroup({
  name,
  color,
  employees,
  canManage,
  canSeeRequests,
  canSeePrivate,
  selfId,
  openId,
  isEditingOpen,
  onToggleRow,
  onEdit,
  onCancelEdit,
  onSave,
  onEndEmployment,
  onDelete,
  onWelcome,
}: {
  name: string;
  color: string | null;
  employees: EmployeeListItem[];
  canManage: boolean;
  canSeeRequests: boolean;
  canSeePrivate: boolean;
  selfId: string | undefined;
  openId: string | null;
  isEditingOpen: boolean;
  onToggleRow: (id: string) => void;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSave: (employee: EmployeeListItem, result: EmployeeFormResult) => Promise<void>;
  onEndEmployment: (employee: EmployeeListItem) => void;
  onDelete: (employee: EmployeeListItem) => void;
  onWelcome: (employee: EmployeeListItem) => void;
}) {
  return (
    // הפס בצד ההתחלה הוא מה שמזהה את הסניף במבט. הצבע אינו לבד:
    // שם הסניף תמיד לצדו (כלל 10).
    <section
      className="grid content-start gap-2 overflow-hidden rounded-md border border-line border-s-4 bg-raised p-3"
      style={color ? { borderInlineStartColor: color } : undefined}
    >
      <header className="flex flex-wrap items-baseline gap-2 border-b border-line pb-2">
        <h3 className="m-0 font-display text-title font-semibold text-ink">{name}</h3>
        <span className="text-body-sm text-muted">
          {employees.length === 1 ? 'עובד/ת אחד/ת' : `${employees.length} עובדים`}
        </span>
      </header>

      {employees.length === 0 ? (
        <p className="m-0 py-2 text-body-sm text-muted">אין עובדים בסניף הזה.</p>
      ) : (
        <ul className="m-0 grid list-none p-0">
          {employees.map((employee) => (
            <li key={employee.id} className="border-b border-line py-1.5 last:border-0 last:pb-0">
              {/*
                שורה אחת, בלי תג סטטוס. התצוגה הזו עונה על "מי אצלי
                בסניף", ו"נכנס/לא נכנס" תפס בה שורה שלמה לכל עובד
                והיה הדבר הרועש במסך. מי שמחפש את זה מוצא אותו
                ברשימה, בעמודת הסטטוס ובמסנן.
              */}
              <button
                type="button"
                onClick={() => onToggleRow(employee.id)}
                aria-expanded={openId === employee.id}
                className="flex w-full cursor-pointer items-baseline gap-2 rounded-sm bg-transparent p-0 text-start hover:bg-cream"
              >
                <ChevronDown
                  size={14}
                  strokeWidth={1.75}
                  aria-hidden="true"
                  className={cn(
                    'shrink-0 self-center text-muted transition-transform',
                    openId === employee.id && 'rotate-180',
                  )}
                />
                <span className="min-w-0 grow truncate text-body">
                  {employee.first_name} {employee.last_name}
                  {employee.id === selfId && (
                    <span className="ms-1 text-label text-muted">(את/ה)</span>
                  )}
                </span>
                {employee.job_titles?.name && (
                  <span className="shrink-0 text-label text-muted">{employee.job_titles.name}</span>
                )}
              </button>

              {openId === employee.id && (
                <div className="pt-2">
                  <EmployeePanel
                    employee={employee}
                    isEditing={isEditingOpen}
                    canManage={canManage}
                    canSeeRequests={canSeeRequests}
                    canSeePrivate={canSeePrivate}
                    isSelf={employee.id === selfId}
                    onEdit={onEdit}
                    onCancelEdit={onCancelEdit}
                    onEndEmployment={() => onEndEmployment(employee)}
                    onDelete={() => onDelete(employee)}
                    onWelcome={() => onWelcome(employee)}
                    onSave={onSave}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * מה שנפתח מתחת לעובד: קודם הפרטים, ובלחיצה על עריכה אותו מקום
 * הופך לטופס. הטופס לא קופץ לראש המסך, ולכן לא מאבדים את המקום
 * ברשימה, ולא רואים טופס שכותרתו עובד אחד ותוכנו עובד אחר.
 */
function EmployeePanel({
  employee,
  isEditing,
  canManage,
  canSeeRequests,
  canSeePrivate,
  isSelf,
  onEdit,
  onCancelEdit,
  onEndEmployment,
  onDelete,
  onWelcome,
  onSave,
}: {
  employee: EmployeeListItem;
  isEditing: boolean;
  canManage: boolean;
  canSeeRequests: boolean;
  canSeePrivate: boolean;
  isSelf: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onEndEmployment: () => void;
  onDelete: () => void;
  onWelcome: () => void;
  onSave: (employee: EmployeeListItem, result: EmployeeFormResult) => Promise<void>;
}) {
  if (isEditing) {
    return (
      <EmployeeForm
        // מזהה כמפתח: בלעדיו מעבר מעובד לעובד משאיר את הטופס על
        // הנתונים הישנים, והשמירה כותבת אותם על הרשומה החדשה
        key={employee.id}
        employee={employee}
        onCancel={onCancelEdit}
        onSubmit={(result) => onSave(employee, result)}
      />
    );
  }

  const extra = (employee.employee_branches ?? [])
    .map((row) => row.branches?.name)
    .filter((name): name is string => Boolean(name) && name !== employee.branches?.name);

  const facts: { label: string; value: string }[] = [
    { label: 'טלפון', value: formatPhone(employee.phone) },
    { label: 'מייל', value: employee.email ?? '—' },
    { label: 'סניף', value: employee.branches?.name ?? '—' },
    { label: 'סניפים נוספים', value: extra.length > 0 ? extra.join(', ') : '—' },
    { label: 'תפקיד', value: employee.job_titles?.name ?? '—' },
    { label: 'הרשאות', value: employee.roles?.name ?? '—' },
    {
      label: 'תחילת עבודה',
      value: employee.start_date ? formatDate(employee.start_date) : '—',
    },
    { label: 'כתובת', value: employee.address ?? '—' },
  ];

  return (
    <div className="@container grid gap-3 rounded-md border border-line bg-raised p-3">
      {/* לפי רוחב הכרטיס ולא לפי המסך: בעמודת סניף צרה שלוש
          עמודות פרטים נדחסות, וב breakpoint של מסך זה נראה תקין */}
      <dl className="m-0 grid gap-x-6 gap-y-2 @sm:grid-cols-2 @lg:grid-cols-3">
        {facts.map((fact) => (
          <div key={fact.label} className="grid gap-0.5">
            <dt className="text-label text-muted">{fact.label}</dt>
            <dd className="m-0 text-body text-ink">{fact.value}</dd>
          </div>
        ))}
      </dl>

      {/* נטענת רק עכשיו, כשהכרטיס פתוח (ראו useEmployeeRequests) */}
      {canSeeRequests && <EmployeeRequests employeeId={employee.id} />}

      {/* מסמכים אישיים. נפתחים בלחיצה ולא מעצמם, כי כל פתיחה
          נרשמת ביומן הגישה (מיגרציה 092). */}
      {canSeePrivate && (
        <PrivateSection employeeId={employee.id} jobTitleId={employee.job_title_id} />
      )}

      {canManage && (
        /**
         * הפריסה נקבעת לפי רוחב הכרטיס ולא לפי רוחב המסך.
         *
         * בתצוגה "לפי סניף" הכרטיס יושב בעמודה צרה, ו flex-wrap
         * שבר את הכפתורים לשורות לא אחידות — שניים בשורה אחת,
         * אחד בשנייה. במסך רחב זה נראה תקין, ולכן breakpoint של
         * מסך לא היה פותר את זה.
         *
         * מסודרים לפי נזק: עריכה, הודעה, סיום העסקה, ורק אחרי
         * כולם המחיקה. כשהכפתורים מוערמים זה מה שקובע על מה
         * לוחצים בטעות.
         */
        <div className="grid gap-2 @sm:flex @sm:flex-wrap">
          <Button variant="secondary" onClick={onEdit}>
            <Pencil size={18} strokeWidth={1.75} aria-hidden="true" />
            עריכת פרטים
          </Button>
          {/* שיחה אישית מול העובד הזה, מתוך הכרטיס שלו (מיגרציה 088) */}
          <DirectMessageButton employeeId={employee.id} to="admin" />
          {/* הודעת הקליטה היא הפנייה הראשונה של המערכת לעובד, ולכן
                המצב שלה גלוי כאן ולא קבור בלוג */}
          <Button variant="secondary" onClick={onWelcome}>
            <Send size={18} strokeWidth={1.75} aria-hidden="true" />
            {employee.invited_at ? 'שליחת הודעת קליטה מחדש' : 'שליחת הודעת קליטה'}
          </Button>
          {!isSelf && (
            <Button variant="secondary" onClick={onEndEmployment}>
              {employee.is_active && !employee.employment_end_date ? (
                <>
                  <UserX size={18} strokeWidth={1.75} aria-hidden="true" />
                  סיום העסקה
                </>
              ) : (
                <>
                  <CalendarDays size={18} strokeWidth={1.75} aria-hidden="true" />
                  שינוי סיום ההעסקה
                </>
              )}
            </Button>
          )}
          {!isSelf && (
            <Button
              variant="ghost"
              aria-label={`מחיקת ${employee.first_name} ${employee.last_name}`}
              onClick={onDelete}
            >
              <Trash2 size={18} strokeWidth={1.75} aria-hidden="true" />
              מחיקה
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/** תא אחד בטבלה, לפי סוג העמודה. */
/** שורת השעות מהשרת, בצורה שהעמודה צריכה. */
function hoursFor(row: HoursRow | undefined): EmployeeHours | undefined {
  if (!row) return undefined;
  return {
    minutes: row.minutes,
    shifts: row.shifts,
    targetHours: row.monthly_target,
    isOverride: row.month_override,
  };
}

/** החודש הנוכחי, מהראשון בו ועד סופו. */
function currentMonthRange(): { from: string; to: string } {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const pad = (value: number) => String(value).padStart(2, '0');
  const last = new Date(year, month + 1, 0).getDate();
  return {
    from: `${year}-${pad(month + 1)}-01`,
    to: `${year}-${pad(month + 1)}-${pad(last)}`,
  };
}

function EmployeeCell({
  column,
  employee,
  sales,
  hours,
}: {
  column: ColumnKey;
  employee: EmployeeListItem;
  sales: EmployeeSales | undefined;
  hours: EmployeeHours | undefined;
}) {
  if (column === 'branch') {
    return (
      <Td className="text-muted">
        <BranchNames employee={employee} />
      </Td>
    );
  }

  if (column === 'status') {
    return (
      <Td>
        <EmployeeStatus employee={employee} />
      </Td>
    );
  }

  if (column === 'salesProgress') {
    // סרגל אחד במקום שלוש עמודות: המילוי נקרא במבט, והמספרים
    // שמתחתיו אומרים בדיוק כמה ומתוך מה.
    //
    // בלי יעד אין סרגל אלא טקסט: מסלול ריק נקרא כאילו העובד/ת
    // ב אפס אחוז מתוך משהו, וזה לא מה שקרה.
    const hasTarget = Boolean(sales?.target);

    return (
      <Td className="min-w-48">
        {hasTarget ? (
          <Progress value={sales?.sold ?? 0} max={sales?.target ?? 0} label={salesLabel(sales)} />
        ) : (
          <span className="text-label text-muted">{salesLabel(sales)}</span>
        )}
      </Td>
    );
  }

  if (column === 'hours') {
    // אותו דפוס כמו המכירות: סרגל כשיש במה למדוד, טקסט כשאין.
    // מסלול ריק נקרא כאילו העובד/ת באפס אחוז מתוך משהו.
    const target = hours?.targetHours ?? null;

    return (
      <Td className="min-w-48">
        {target === null ? (
          <span className="text-label text-muted">{hoursLabel(hours)}</span>
        ) : (
          <Progress
            value={Math.min(hours?.minutes ?? 0, target * 60)}
            max={target * 60}
            label={hoursLabel(hours)}
          />
        )}
      </Td>
    );
  }

  const isLatin = column === 'phone' || column === 'comax';

  return (
    <Td className={cn('text-muted', isLatin && 'tabular whitespace-nowrap')}>
      {/*
        ה dir יושב על הטקסט ולא על התא. על התא הוא הופך את ה start
        שלו לשמאל, בזמן שהכותרת נשארת RTL ומיושרת ימינה, והעמודה
        נראית מוסטת ביחס לכותרת שלה.
      */}
      {isLatin ? <span dir="ltr">{cellValue(column, employee)}</span> : cellValue(column, employee)}
    </Td>
  );
}

/**
 * בחירת העמודות. מנהל כוח אדם ומנהל מכירות מסתכלים על אותו מסך
 * ומחפשים דברים שונים, וטבלה שמנסה לרצות את שניהם אינה קריאה
 * לאיש.
 */
function ColumnsDialog({
  available,
  shown,
  onToggle,
  onReset,
  onClose,
}: {
  available: ColumnKey[];
  shown: ColumnKey[];
  onToggle: (key: ColumnKey, on: boolean) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-40 grid items-end bg-charcoal/50 p-0 sm:place-items-center sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="עמודות בטבלה"
        className="grid max-h-[85dvh] w-full gap-3 overflow-y-auto rounded-t-lg bg-surface p-4 sm:max-w-md sm:rounded-lg"
      >
        <div className="flex items-start justify-between gap-2">
          <h2 className="m-0 font-display text-display-m font-semibold">עמודות בטבלה</h2>
          <button
            type="button"
            aria-label="סגירה"
            onClick={onClose}
            className="grid size-11 shrink-0 cursor-pointer place-items-center rounded-sm bg-transparent text-ink hover:bg-cream"
          >
            <X size={20} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>

        <p className="m-0 text-body-sm text-muted">השם תמיד מוצג. הבחירה נשמרת בדפדפן הזה.</p>

        <div className="grid gap-1">
          {available.map((key) => (
            <label
              key={key}
              className="flex min-h-11 cursor-pointer items-center gap-3 rounded-sm px-1 text-body hover:bg-cream"
            >
              <input
                type="checkbox"
                checked={shown.includes(key)}
                onChange={(event) => onToggle(key, event.target.checked)}
                className="size-5"
              />
              {COLUMN_LABELS[key]}
            </label>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="primary" onClick={onClose}>
            סיימתי
          </Button>
          <Button variant="secondary" onClick={onReset}>
            חזרה לברירת המחדל
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * סיום העסקה, עם תאריך.
 *
 * ברוב המקרים יודעים מראש מתי מישהו מסיים, ורוצים לקבוע את זה
 * עכשיו ולא לזכור ביום עצמו. התאריך הוא יום העבודה האחרון, והגישה
 * נסגרת אחריו.
 *
 * כברירת מחדל היום: מי שפותח את החלון בדרך כלל מסיים עכשיו, ומי
 * שמתכנן קדימה ישנה תאריך.
 */
function EmploymentEndDialog({
  employee,
  onClose,
  onConfirm,
}: {
  employee: EmployeeListItem;
  onClose: () => void;
  onConfirm: (date: string | null) => void;
}) {
  const today = toDateKey(new Date());
  const [date, setDate] = useState(employee.employment_end_date ?? today);
  const isScheduled = Boolean(employee.employment_end_date);
  const isFuture = date > today;

  return (
    <div className="fixed inset-0 z-40 grid items-end bg-charcoal/50 p-0 sm:place-items-center sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="סיום העסקה"
        className="grid max-h-[85dvh] w-full gap-3 overflow-y-auto rounded-t-lg bg-surface p-4 sm:max-w-md sm:rounded-lg"
      >
        <div className="flex items-start justify-between gap-2">
          <h2 className="m-0 font-display text-display-m font-semibold">
            סיום העסקה · {employee.first_name} {employee.last_name}
          </h2>
          <button
            type="button"
            aria-label="סגירה"
            onClick={onClose}
            className="grid size-11 shrink-0 cursor-pointer place-items-center rounded-sm bg-transparent text-ink hover:bg-cream"
          >
            <X size={20} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>

        <div className="grid gap-1">
          <label htmlFor="employment-end" className="text-label font-medium">
            יום העבודה האחרון
          </label>
          <input
            id="employment-end"
            type="date"
            dir="ltr"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            className={FIELD}
          />
          <p className="m-0 text-body-sm text-muted">
            {isFuture
              ? `הגישה תיסגר ב ${formatDate(date)} בסוף היום. עד אז הכל ממשיך כרגיל.`
              : 'הגישה תיסגר עכשיו: הסידור, ההודעות והלומדות לא יהיו זמינים יותר.'}
          </p>
        </div>

        <p className="m-0 text-body-sm text-muted">
          הנתונים נשמרים. אפשר לבטל ולהחזיר לפעילות בכל שלב.
        </p>

        <div className="flex flex-wrap gap-2">
          <Button variant="primary" disabled={date === ''} onClick={() => onConfirm(date)}>
            {isFuture ? 'קביעת סיום ההעסקה' : 'סיום ההעסקה עכשיו'}
          </Button>
          {isScheduled && (
            <Button variant="secondary" onClick={() => onConfirm(null)}>
              <RotateCcw size={18} strokeWidth={1.75} aria-hidden="true" />
              ביטול והחזרה לפעילות
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            ביטול
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * מסמכים אישיים בכרטיס העובד.
 *
 * סגור כברירת מחדל ונפתח בלחיצה מפורשת: פתיחה היא צפייה בת"ז
 * ובפרטי בנק, והיא נרשמת ביומן. פאנל שנפתח לבד היה הופך את
 * היומן לרעש.
 */
function PrivateSection({
  employeeId,
  jobTitleId,
}: {
  employeeId: string;
  jobTitleId: string | null;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="grid gap-3">
      <div>
        <Button variant="secondary" onClick={() => setIsOpen((open) => !open)}>
          <ShieldCheck size={18} strokeWidth={1.75} aria-hidden="true" />
          {isOpen ? 'סגירת המסמכים האישיים' : 'מסמכים אישיים וחוזה'}
        </Button>
      </div>

      {isOpen && (
        <>
          <PrivateDetails employeeId={employeeId} />
          <ContractsPanel employeeId={employeeId} jobTitleId={jobTitleId} />
        </>
      )}
    </div>
  );
}
